import { MarkerType } from '@xyflow/react';

const defaultPolicyEdgeMarkerSize = 14;

export const POLICY_CONNECTION_RULE_REASON = {
    SELF_CONNECTION: 'SELF_CONNECTION',
    DUPLICATE_CONNECTION: 'DUPLICATE_CONNECTION',
    MISSING_NODE: 'MISSING_NODE',
    SAME_TYPE: 'SAME_TYPE',
    DATA_TARGET_REQUIRES_PATH_SOURCE: 'DATA_TARGET_REQUIRES_PATH_SOURCE',
    PATH_ACTION_REQUIRED: 'PATH_ACTION_REQUIRED',
    INCOMPLETE_APPLICATION: 'INCOMPLETE_APPLICATION',
    PATH_CAN_CONNECT_ONLY_DATA: 'PATH_CAN_CONNECT_ONLY_DATA',
    APPLICATION_CAN_CONNECT_ONLY_RESTFULAPI: 'APPLICATION_CAN_CONNECT_ONLY_RESTFULAPI',
    CARDINALITY_VIOLATION: 'CARDINALITY_VIOLATION',
    ACTION_MISMATCH: 'ACTION_MISMATCH',
};

export const canPolicyConnectByNodeType = (sourceType, targetType) => {
    if (sourceType === 'path' && targetType === 'data') {
        return true;
    }

    if (sourceType === 'application' && targetType === 'restfulapi') {
        return true;
    }

    if (sourceType === 'restfulapi' && targetType === 'application') {
        return true;
    }

    return false;
};

export const shouldShowPolicyConnectHandle = (startNode, candidateNode) => {
    if (!startNode || !candidateNode) {
        return false;
    }

    return canPolicyConnectByNodeType(startNode.type, candidateNode.type);
};

export const validateNodePair = ({ params, sourceNode, targetNode, edges = [] }) => {
    if (params?.source === params?.target) {
        return {
            valid: false,
            reason: POLICY_CONNECTION_RULE_REASON.SELF_CONNECTION,
        };
    }

    const isDuplicate = edges.some(
        (edge) => edge.source === params?.source && edge.target === params?.target
    );
    if (isDuplicate) {
        return {
            valid: false,
            reason: POLICY_CONNECTION_RULE_REASON.DUPLICATE_CONNECTION,
        };
    }

    if (!sourceNode || !targetNode) {
        return {
            valid: false,
            reason: POLICY_CONNECTION_RULE_REASON.MISSING_NODE,
        };
    }

    if (sourceNode.type === targetNode.type) {
        return {
            valid: false,
            reason: POLICY_CONNECTION_RULE_REASON.SAME_TYPE,
        };
    }

    if (sourceNode.type !== 'path' && targetNode.type === 'data') {
        return {
            valid: false,
            reason: POLICY_CONNECTION_RULE_REASON.DATA_TARGET_REQUIRES_PATH_SOURCE,
        };
    }

    if (!sourceNode?.data?.general_properties?.action && sourceNode.type === 'path') {
        return {
            valid: false,
            reason: POLICY_CONNECTION_RULE_REASON.PATH_ACTION_REQUIRED,
        };
    }

    if (sourceNode.type === 'application' && targetNode.type === 'restfulapi') {
        if ((sourceNode?.data?.error_text || []).length > 0) {
            return {
                valid: false,
                reason: POLICY_CONNECTION_RULE_REASON.INCOMPLETE_APPLICATION,
            };
        }
    }

    if (sourceNode.type === 'path' && targetNode.type !== 'data') {
        return {
            valid: false,
            reason: POLICY_CONNECTION_RULE_REASON.PATH_CAN_CONNECT_ONLY_DATA,
        };
    }

    if (sourceNode.type === 'application' && targetNode.type !== 'restfulapi') {
        return {
            valid: false,
            reason: POLICY_CONNECTION_RULE_REASON.APPLICATION_CAN_CONNECT_ONLY_RESTFULAPI,
        };
    }

    return { valid: true };
};

export const validateConnectionCardinality = ({ sourceNode, targetNode, edges = [] }) => {
    if (sourceNode?.type === 'path' && targetNode?.type === 'data') {
        const existingSourceEdge = edges.some((edge) => edge.source === sourceNode.id);
        const existingTargetEdge = edges.some((edge) => edge.target === targetNode.id);

        if (existingSourceEdge || existingTargetEdge) {
            return {
                valid: false,
                reason: POLICY_CONNECTION_RULE_REASON.CARDINALITY_VIOLATION,
            };
        }
    }

    return { valid: true };
};

export const validateActionCompatibility = ({ sourceNode, targetNode }) => {
    if (sourceNode?.type === 'path' && targetNode?.type === 'data') {
        const sourceAction = sourceNode?.data?.general_properties?.action;
        const targetAction = targetNode?.data?.general_properties?.action;

        const isValidAction = (
            ((sourceAction === 'GET' || sourceAction === 'POST') && targetAction === 'SELECT')
            || (sourceAction === 'POST' && targetAction === 'INSERT')
            || (sourceAction === 'PUT' && targetAction === 'UPDATE')
            || (sourceAction === 'DELETE' && targetAction === 'DELETE')
        );

        if (!isValidAction) {
            return {
                valid: false,
                reason: POLICY_CONNECTION_RULE_REASON.ACTION_MISMATCH,
            };
        }
    }

    return { valid: true };
};

export const evaluatePolicyConnection = ({ params, sourceNode, targetNode, edges = [] }) => {
    const nodePairValidation = validateNodePair({
        params,
        sourceNode,
        targetNode,
        edges,
    });
    if (!nodePairValidation.valid) {
        return nodePairValidation;
    }

    const cardinalityValidation = validateConnectionCardinality({
        sourceNode,
        targetNode,
        edges,
    });
    if (!cardinalityValidation.valid) {
        return cardinalityValidation;
    }

    const actionCompatibilityValidation = validateActionCompatibility({
        sourceNode,
        targetNode,
    });
    if (!actionCompatibilityValidation.valid) {
        return actionCompatibilityValidation;
    }

    return { valid: true };
};

export const convertActionPathToActionData = (action) => {
    if (action === 'GET') {
        return 'SELECT';
    }
    if (action === 'POST') {
        return 'INSERT';
    }
    if (action === 'PUT') {
        return 'UPDATE';
    }
    return 'DELETE';
};

export const calculateInheritedEnvironment = ({ sourceNode, targetNode, apiParentNode }) => {
    if (!sourceNode || !targetNode) {
        return null;
    }

    if (sourceNode.type === 'application' && targetNode.type === 'restfulapi') {
        const newResources = {
            ...targetNode.data.environment_properties,
            resources: [
                ...targetNode.data.environment_properties.resources,
                {
                    id: sourceNode.id,
                    name: sourceNode.data.general_properties.name,
                }
            ]
        };

        return {
            type: 'application-to-restfulapi',
            inheritedResources: newResources,
            targetNode: {
                ...targetNode,
                data: {
                    ...targetNode.data,
                    environment_properties: newResources,
                }
            }
        };
    }

    if (sourceNode.type === 'path' && targetNode.type === 'data') {
        const dataAction = convertActionPathToActionData(sourceNode?.data?.general_properties?.action);

        const newTargetNode = {
            ...targetNode,
            data: {
                ...targetNode.data,
                connect_with_path: sourceNode.id,
                general_properties: {
                    ...targetNode.data.general_properties,
                    action: dataAction,
                },
                environment_properties: {
                    ...targetNode.data.environment_properties,
                    resources: [
                        ...targetNode.data.environment_properties.resources,
                        {
                            id: apiParentNode?.id,
                            name: apiParentNode?.data?.general_properties?.name,
                        }
                    ]
                }
            }
        };

        const clearErr = (newTargetNode.data.error_text || []).filter((err) => (
            err !== 'Cannot proceed: no connection to a path node.'
            && err !== 'No RESTful API node connection. Please connect this node to a RESTful API node.'
        ));
        newTargetNode.data.error_text = clearErr;

        return {
            type: 'path-to-data',
            targetNode: newTargetNode,
        };
    }

    return null;
};

export const createPolicyEdge = ({ params, sourceNode, targetNode, edgeColor }) => {
    const resolvedEdgeColor = edgeColor || 'gray';

    return {
        ...params,
        id: `${sourceNode.id}-${targetNode.id}`,
        style: {
            stroke: resolvedEdgeColor,
            strokeWidth: 2,
        },
        markerEnd: {
            type: MarkerType.ArrowClosed,
            width: defaultPolicyEdgeMarkerSize,
            height: defaultPolicyEdgeMarkerSize,
            color: resolvedEdgeColor,
        }
    };
};
